import { withoutActWarning } from '@w/utils-fe'
import { useEffect, useRef, useState } from 'react'
import { toPcmWav } from '@w/media-processing'
import { UFile } from '@w/utils'
import { IconButton } from '@w/ui/components/icon-button'
import { MicIcon } from '@w/ui/components/icon'
import { Tooltip, TooltipPopup, TooltipTrigger } from '@w/ui/components/tooltip'
import { cn } from '@w/ui/lib/utils'

export interface AudioRecorderProps {
  /** Called with the recorded audio once the user stops recording. */
  onRecorded: (file: UFile) => void
  className?: string
  buttonClassName?: string
  size?: 'sm' | 'default' | 'lg'
  variant?: 'text' | 'filled' | 'outlined'
}

interface RecordingControls {
  start: () => void
  stop: () => Promise<UFile>
  dispose: () => void
}

/** Toggle recorder. The second press stops recording and yields its audio. */
export function AudioRecorder({
  onRecorded,
  className,
  buttonClassName,
  size,
  variant,
}: AudioRecorderProps) {
  const [recording, setRecording] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [elapsedMs, setElapsedMs] = useState(0)
  // oxlint-disable-next-line unicorn/no-useless-undefined -- useRef requires an explicit initial value argument
  const controlsRef = useRef<RecordingControls | undefined>(undefined)
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    // oxlint-disable-next-line unicorn/no-useless-undefined -- useRef requires an explicit initial value argument
    undefined,
  )
  const consumedPointerPressRef = useRef(false)
  const busyRef = useRef(false)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (intervalRef.current) clearInterval(intervalRef.current)
      controlsRef.current?.dispose()
      controlsRef.current = undefined
    }
  }, [])

  const start = async () => {
    if (controlsRef.current) return

    const controls = await recordAudio()
    if (!mountedRef.current) { controls.dispose(); return }
    controlsRef.current = controls
    withoutActWarning(() => { setElapsedMs(0); setRecording(true) })
    intervalRef.current = setInterval(() => {
      withoutActWarning(() => { setElapsedMs((ms) => ms + 100) })
    }, 100)
    controls.start()
  }

  const stop = async () => {
    setElapsedMs(0)
    if (intervalRef.current) clearInterval(intervalRef.current)

    if (!controlsRef.current) return
    const file = await controlsRef.current.stop()
    controlsRef.current = undefined
    if (mountedRef.current) {
      onRecorded(file)
      withoutActWarning(() => { setRecording(false) })
    }
  }

  const seconds = Math.floor(elapsedMs / 1000)
  const tenths = Math.floor((elapsedMs % 1000) / 100)
  const toggle = () => {
    if (busyRef.current) return
    busyRef.current = true
    setError(null)
    void (controlsRef.current ? stop() : start()).catch((cause: unknown) => {
      if (!mountedRef.current) return
      withoutActWarning(() => { setRecording(false) })
      if (intervalRef.current) clearInterval(intervalRef.current)
      controlsRef.current?.dispose()
      controlsRef.current = undefined
      withoutActWarning(() => { setError(cause instanceof Error ? cause.message : String(cause)) })
    }).finally(() => { busyRef.current = false })
  }

  return (
    <div>
      <Tooltip open={recording}>
        <TooltipTrigger
          render={
            <div className={cn('inline-flex rounded-full', className)}>
              <IconButton
                aria-label={recording ? 'Stop recording' : 'Record'}
                className={buttonClassName}
                size={size}
                toggled={recording}
                variant={variant}
                onPointerUp={() => {
                  consumedPointerPressRef.current = true
                  toggle()
                }}
                onClick={() => {
                  if (consumedPointerPressRef.current) {
                    consumedPointerPressRef.current = false
                    return
                  }
                  toggle()
                }}
              >
                <MicIcon />
                Record
              </IconButton>
            </div>
          }
        />
        <TooltipPopup sideOffset={24}>
          {seconds}.{tenths}
        </TooltipPopup>
      </Tooltip>
      {error && <span role="alert">{error}</span>}
    </div>
  )
}

async function recordAudio(): Promise<RecordingControls> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  const mediaRecorder = new MediaRecorder(stream)
  const audioChunks: Blob[] = []
  let startedAtMs = 0

  mediaRecorder.addEventListener('dataavailable', (event) => {
    audioChunks.push(event.data)
  })

  const start = () => {
    startedAtMs = Date.now()
    mediaRecorder.start()
  }

  const stop = () =>
    new Promise<UFile>((resolveStop, rejectStop) => {
      mediaRecorder.addEventListener('stop', () => {
        const blob = new Blob(audioChunks, { type: mediaRecorder.mimeType })
        const durationMs = Math.max(1, Math.round(Date.now() - startedAtMs))
        const file = new UFile(blob, {
          path: `recording.${_extension(mediaRecorder.mimeType)}`,
          meta: { durationMs },
        })
        // AAC/MPEG-4 is already a portable recording. Preserve its encoded bytes.
        if (/^audio\/(mp4|m4a)(;|$)/i.test(mediaRecorder.mimeType)) resolveStop(file)
        else void _pcmRecording(file).then(resolveStop, rejectStop)
      })
      mediaRecorder.addEventListener('error', () => { rejectStop(new Error('Audio recording failed')) }, { once: true })
      // https://stackoverflow.com/questions/44274410/mediarecorder-stop-doesnt-clear-the-recording-icon-in-the-tab
      for (const track of stream.getTracks()) track.stop()
      mediaRecorder.stop()
    })

  const dispose = () => {
    for (const track of stream.getTracks()) track.stop()
    if (mediaRecorder.state !== 'inactive') mediaRecorder.stop()
  }
  return { start, stop, dispose }
}

function _extension(mimeType: string): string {
  const extension = mimeType.split('/')[1]?.split(';')[0] ?? 'audio'
  return extension === 'mp4' ? 'm4a' : extension
}

/** Avoid decoding audio that already meets the PCM output contract. */
async function _pcmRecording(file: UFile): Promise<UFile> {
  if (file.type.split(';')[0] === 'audio/wav') {
    const bytes = await file.uint8Array()
    if (bytes.length >= 44) {
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
      if (view.getUint32(0) === 0x52494646 && view.getUint32(8) === 0x57415645 &&
        view.getUint32(12) === 0x666d7420 && view.getUint32(16, true) === 16 &&
        view.getUint16(20, true) === 1 && view.getUint16(22, true) === 1 &&
        view.getUint32(24, true) === 16000 && view.getUint16(34, true) === 16 &&
        view.getUint32(36) === 0x64617461 && view.getUint32(40, true) === bytes.length - 44) {
        return new UFile(file.blob, {
          path: file.path,
          type: 'audio/wav; codecs=audio/pcm; samplerate=16000',
          meta: file.meta,
        })
      }
    }
  }
  return toPcmWav(file)
}
